/**
 * outage-filter.js
 *
 * Dynamicky vytvoří filtry nad kartami odstávek (.card-outage) a filtruje je.
 * Každý filtr se sestaví z hodnot, které se skutečně vyskytují v kartách.
 * Filtry se kombinují (logické AND) a jejich stav se synchronizuje s URL:
 *
 *   /odstavky/?typ=porucha
 *   /odstavky/?typ=planovana-odstavka&energie=teplo
 *
 * Použití:
 *   <script type="module">
 *     import { initOutageFilters } from '/assets/js/outage-filter.js';
 *     initOutageFilters();
 *   </script>
 */

const DEFAULTS = {
  /** Karta odstávky */
  cardSelector: ".card-outage",
  /** Obal karty, který se skrývá (sloupec gridu); když neexistuje, skrývá se samotná karta */
  itemSelector: ".col",
  /** Kontejner, do kterého se filtry vykreslí */
  filterContainerSelector: ".filters-container",
  /** Popisek položky "zobrazit vše" */
  allLabel: "Vše",
  /** Třída aktivní položky (<li> i <a>) */
  activeClass: "active",
  /** Text při prázdném výsledku */
  emptyMessage: "Zvoleným filtrům neodpovídá žádná odstávka.",
  /** Synchronizace stavu filtrů s URL (query parametry) */
  syncUrl: true,
  /** 'replace' = filtr nepřidává záznamy do historie; 'push' = tlačítko Zpět vrací předchozí filtr */
  historyMode: "replace",
  /** Délka fade animace při filtrování v ms; 0 = bez animace (respektuje se i prefers-reduced-motion) */
  animationDuration: 250,
  /**
   * Definice filtrů.
   *  - key:              interní klíč (i data-filter na <ul>)
   *  - param:            název query parametru v URL (bez něj se filtr do URL neukládá)
   *  - label:            aria-label seznamu (přístupnost)
   *  - attribute:        název v dataset (data-outage-type -> outageType)
   *  - fallbackSelector: volitelně – když data atribut chybí/je prázdný, vezme se text z tohoto elementu
   */
  filters: [
    {
      key: "type",
      param: "typ",
      label: "Filtr podle typu odstávky",
      attribute: "outageType",
      fallbackSelector: ".card-outage-type",
    },
    {
      key: "energy",
      param: "energie",
      label: "Filtr podle druhu energie",
      attribute: "outageEnergy",
    },
  ],
};

/** "Plánovaná odstávka" -> "planovana-odstavka" (hodnota v URL bez diakritiky). */
function slugify(text) {
  return String(text)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Načte hodnotu filtru z karty. */
function readValue(card, filter) {
  const fromData = card.dataset[filter.attribute]?.trim();
  if (fromData) return fromData;

  if (filter.fallbackSelector) {
    return card.querySelector(filter.fallbackSelector)?.textContent.trim() || "";
  }
  return "";
}

/** Vytvoří <li><a href="#">…</a></li>. Prázdná hodnota = "Vše". */
function createItem(label, value, isActive, activeClass) {
  const li = document.createElement("li");
  const a = document.createElement("a");

  a.href = "#";
  a.textContent = label;
  a.dataset.value = value;

  if (isActive) {
    li.classList.add(activeClass);
    a.classList.add(activeClass);
    a.setAttribute("aria-current", "true");
  }

  li.append(a);
  return li;
}

/** Vytvoří <ul> jednoho filtru. */
function createList(filter, values, options) {
  const ul = document.createElement("ul");
  ul.dataset.filter = filter.key;
  if (filter.label) ul.setAttribute("aria-label", filter.label);

  ul.append(createItem(options.allLabel, "", true, options.activeClass));
  values.forEach((value) => ul.append(createItem(value, value, false, options.activeClass)));

  return ul;
}

/** Označí aktivní položku v jednom seznamu. */
function setActive(ul, value, activeClass) {
  ul.querySelectorAll("li").forEach((li) => {
    const a = li.querySelector("a");
    const isActive = a.dataset.value === value;

    li.classList.toggle(activeClass, isActive);
    a.classList.toggle(activeClass, isActive);
    if (isActive) a.setAttribute("aria-current", "true");
    else a.removeAttribute("aria-current");
  });
}

/**
 * Inicializace filtrů.
 * @param {Partial<typeof DEFAULTS>} [userOptions]
 */
export function initOutageFilters(userOptions = {}) {
  const options = { ...DEFAULTS, ...userOptions };

  const container = document.querySelector(options.filterContainerSelector);
  const cards = [...document.querySelectorAll(options.cardSelector)];
  if (!container || cards.length === 0) return;

  // Data karet: element, který se skrývá, + hodnoty všech filtrů
  const items = cards.map((card) => ({
    element: card.closest(options.itemSelector) ?? card,
    values: Object.fromEntries(options.filters.map((filter) => [filter.key, readValue(card, filter)])),
  }));

  // Aktuální stav: '' = všechny hodnoty
  const state = Object.fromEntries(options.filters.map((filter) => [filter.key, ""]));

  // Zpráva pro prázdný výsledek
  const emptyMessage = document.createElement("p");
  emptyMessage.className = "outage-empty";
  emptyMessage.textContent = options.emptyMessage;
  emptyMessage.hidden = true;
  items[0].element.parentElement.after(emptyMessage);

  // Statické seznamy nahradíme dynamickými (nadpis "Filtry" zůstává)
  container.querySelectorAll("ul").forEach((ul) => ul.remove());

  // Vykreslené filtry: { filter, ul, slugs: Map<slug, hodnota> }
  const rendered = [];

  for (const filter of options.filters) {
    // Unikátní hodnoty v pořadí, v jakém se poprvé objeví; prázdné se ignorují
    const values = [...new Set(items.map((item) => item.values[filter.key]).filter(Boolean))];
    if (values.length < 2) continue; // filtr bez hodno, nebo jen s jednou hodnotou se nevykreslí

    const ul = createList(filter, values, options);
    container.append(ul);
    rendered.push({ filter, ul, slugs: new Map(values.map((v) => [slugify(v), v])) });
  }

  /* ---------- URL ---------- */

  /** Sestaví URL odpovídající danému stavu (ostatní parametry se zachovají, hash se smaže). */
  function buildUrl(nextState) {
    const url = new URL(window.location.href);
    url.hash = "";

    for (const { filter } of rendered) {
      if (!filter.param) continue;
      const value = nextState[filter.key];
      if (value) url.searchParams.set(filter.param, slugify(value));
      else url.searchParams.delete(filter.param);
    }
    return url;
  }

  /** Přečte stav filtrů z aktuální URL; neznámé hodnoty se ignorují. */
  function readUrlState() {
    const params = new URLSearchParams(window.location.search);
    const next = {};

    for (const { filter, slugs } of rendered) {
      const raw = filter.param ? params.get(filter.param) : null;
      next[filter.key] = raw ? (slugs.get(slugify(raw)) ?? "") : "";
    }
    return next;
  }

  /** Zapíše aktuální stav do adresního řádku. */
  function writeUrl() {
    if (!options.syncUrl) return;

    const url = buildUrl(state);
    if (url.href === window.location.href) return;

    const method = options.historyMode === "push" ? "pushState" : "replaceState";
    window.history[method](null, "", url.href);
  }

  /** Odkazy ve filtru dostanou skutečné URL (funguje kopírování odkazu, otevření v nové záložce). */
  function updateLinks() {
    if (!options.syncUrl) return;

    for (const { filter, ul } of rendered) {
      ul.querySelectorAll("a[data-value]").forEach((a) => {
        a.href = buildUrl({ ...state, [filter.key]: a.dataset.value }).href;
      });
    }
  }

  /* ---------- Filtrování ---------- */

  let currentRun = 0; // číslo posledního filtrování – starší animace se po přerušení ukončí
  const animations = new Set();

  const matches = (item) =>
    options.filters.every((filter) => !state[filter.key] || item.values[filter.key] === state[filter.key]);

  function canAnimate() {
    return (
      options.animationDuration > 0 &&
      typeof Element.prototype.animate === "function" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  /** Přehraje fade (opacity from -> to). Vrátí Animation, nebo null, když byla zrušena. */
  async function play(element, from, to) {
    const animation = element.animate([{ opacity: from }, { opacity: to }], {
      duration: options.animationDuration,
      easing: "ease",
      fill: "forwards",
    });
    animations.add(animation);

    try {
      await animation.finished;
    } catch {
      return null; // zrušeno novým filtrováním
    }
    return animation; // zůstává v `animations`, další filtrování ji vždy zruší (uvolní fill)
  }

  /**
   * Aplikuje aktuální stav filtrů.
   * 1) mizející karty se vyfadují a skryjí,
   * 2) přicházející karty se zobrazí a vyfadují dovnitř.
   */
  async function apply({ animate = true } = {}) {
    const run = ++currentRun;

    // Případné běžící animace z předchozího kliknutí zrušíme
    animations.forEach((animation) => animation.cancel());
    animations.clear();

    const leaving = [];
    const entering = [];
    let visibleCount = 0;

    for (const item of items) {
      const visible = matches(item);
      if (visible) visibleCount += 1;

      if (!item.element.hidden && !visible) leaving.push(item);
      else if (item.element.hidden && visible) entering.push(item);
    }

    if (visibleCount > 0) emptyMessage.hidden = true;

    // Bez animace: okamžitá změna
    if (!animate || !canAnimate()) {
      leaving.forEach((item) => (item.element.hidden = true));
      entering.forEach((item) => (item.element.hidden = false));
      emptyMessage.hidden = visibleCount > 0;
      return;
    }

    // 1) Fade-out mizejících karet
    if (leaving.length > 0) {
      const fades = await Promise.all(leaving.map((item) => play(item.element, 1, 0)));
      if (run !== currentRun) return; // mezitím proběhlo nové filtrování

      leaving.forEach((item) => (item.element.hidden = true));
      fades.forEach((animation) => animation?.cancel()); // uvolní fill až po skrytí
    }

    // 2) Fade-in přicházejících karet (a případné zprávy o prázdném výsledku)
    const fadeIn = entering.map((item) => item.element);
    if (visibleCount === 0) {
      emptyMessage.hidden = false;
      fadeIn.push(emptyMessage);
    }

    fadeIn.forEach((element) => (element.hidden = false));
    const fades = await Promise.all(fadeIn.map((element) => play(element, 0, 1)));
    if (run !== currentRun) return;

    fades.forEach((animation) => animation?.cancel());
  }

  /** Nastaví celý stav najednou (kliknutí, načtení stránky, popstate). */
  function setState(next, { animate = true } = {}) {
    for (const { filter, ul } of rendered) {
      state[filter.key] = next[filter.key] ?? "";
      setActive(ul, state[filter.key], options.activeClass);
    }
    updateLinks();
    apply({ animate });
  }

  // Delegace událostí – jeden listener pro všechny filtry
  container.addEventListener("click", (event) => {
    const link = event.target.closest("ul[data-filter] a[data-value]");
    if (!link || !container.contains(link)) return;

    event.preventDefault();

    const key = link.closest("ul[data-filter]").dataset.filter;
    setState({ ...state, [key]: link.dataset.value });
    writeUrl();
  });

  if (options.syncUrl) {
    // Tlačítko Zpět/Vpřed (relevantní při historyMode: 'push')
    window.addEventListener("popstate", () => setState(readUrlState()));
    // Počáteční stav z URL
    setState(readUrlState(), { animate: false });
  } else {
    apply({ animate: false });
  }
}
